import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { fetchContentRecommendations } from "src/redux/actions/assets";
import { Asset} from "src/types/assets";
import { ScrollableContainer } from "src/components/scroller";
import Tooltip from "src/components/tooltip";
import Thumbnail from "src/components/thumbnail";
import { FileService } from "src/utils/file.service";

import _ from "lodash";
import cx from "classnames";
import styles from "./recommendedAssets.module.scss";
import Text from "src/components/text";


interface AssetEntity {
  id: string;
  entityType: string;
}

interface EmailConfig {
  addAsset: {
    option: any | null;
    entities: AssetEntity[];
  };
}


interface RecommendedAssetsProps {
  // eslint-disable-next-line no-unused-vars
  onAssetAdd: (asset:Asset) => void;
  emailConfigState : EmailConfig;
  isSidePanelOpen : boolean;
  dialog : boolean;
  showRecommendedAssets : boolean;
  handleRecommendedAssets: () => void;
}


const RecommendedAssets: React.FC<RecommendedAssetsProps> = ({ onAssetAdd, emailConfigState, isSidePanelOpen , dialog , showRecommendedAssets ,handleRecommendedAssets}) => {

  const dispatch = useDispatch();

  const [expanded, setExpanded] = useState(true);

  const { assets, loading, fetched } = useSelector((state: any) => {
    const recommendedAssets = _.get(state, 'asset.recommendedAssets', {});

    return {
      assets: recommendedAssets.items,
      loading: recommendedAssets.loading,
      fetched : recommendedAssets.fetched
    }
  }
  );

  const LeftArrow: React.FC<any> = () => {
    return (
      <div className={cx(styles.arrow, styles.left)}>
        <i className="paperflite-left-arrow"></i>
      </div>
    );
  }

  const RightArrow: React.FC<any> = () => {
    return (
      <div className={cx(styles.arrow, styles.right)}>
        <i className="paperflite-right-arrow"></i>
      </div>
    );
  }

  const filteredAssets = useMemo(() => {
    if (!assets || !emailConfigState.addAsset.entities) {
      return [];
    }
  
    const existingAssetIds = emailConfigState.addAsset.entities.map(entity => entity.id);
  
    return assets.filter(asset => !existingAssetIds.includes(asset.id));
  
  }, [assets, emailConfigState.addAsset.entities]);

  return (
    filteredAssets.length > 0 ? (
    <div className={cx(styles.recommendedAssets, { [styles.recommendedAssetsExpanded]: showRecommendedAssets, [styles.showDivider]: !showRecommendedAssets })}>
        <div className={cx(styles.header, {
          [styles.sidePanelVisible]: isSidePanelOpen,
          [styles.dialogBox]: dialog
        })} onClick={handleRecommendedAssets}>
        <div className={styles.section}>
          <div className={styles.title}>Recommended Assets</div>
        </div>
        <div
          className={cx(styles.dropdownContainer)}
          onClick={handleRecommendedAssets}
        >
          <i className={cx("paperflite-dropdown", styles.dropdownIcon, { [styles.inverted]: !showRecommendedAssets })}></i>
          {(!isSidePanelOpen && !dialog) && (
            <span className={styles.hoverText}>{showRecommendedAssets ? "hide section" : "show section"}</span>
          )}
        </div>


      </div>
      <div className={styles.body}>
        {loading && !fetched && showRecommendedAssets &&  (
          <div className={styles.skeletonWrapper}>
            {_.times(
              isSidePanelOpen && dialog ? 3 : isSidePanelOpen ? 4 : dialog ? 3 : 6,
              (i) => <Placeholder key={i} />
            )}
          </div>
        )}
        {!loading  && (
          <ScrollableContainer
            distance={200}
            leftArrowComponent={LeftArrow}
            rightArrowComponent={RightArrow}
            wrapperClassName={cx(styles.customWrapper, {
              [styles.sidePanelOpen]: isSidePanelOpen,
              [styles.dialogOpen]: dialog
            })}
          >
            {filteredAssets.map((asset) => (
              <div className={styles.asset} key={asset.id}>
                  {asset && <AssetCard asset={asset} editable={false} onAssetAdd={() => onAssetAdd(asset)}/>}
              </div>
            ))}
          </ScrollableContainer>
        )}
      </div>
    </div>
  ): <></>);
}

export default RecommendedAssets;

interface AssetCardProps {
  asset: Asset;
  editable: boolean;
  // eslint-disable-next-line no-unused-vars
  onAssetAdd: (asset:Asset) => void;
}


const AssetCard: React.FC<AssetCardProps> = ({ asset , onAssetAdd }) => {

  const dispatch = useDispatch();

  const handleAddAsset = (asset) => {
    onAssetAdd(asset);
    if (asset.id) {
      dispatch(fetchContentRecommendations([asset.id]));
    }
    else {
      dispatch(fetchContentRecommendations([]));
    }
  }

  const fileType = useMemo(() => {
    if (asset != null) {
      return FileService.getFileType(asset.metadata);
    } else {
      return;
    }
  }, [asset]);

  const internal = !_.get(asset, 'settings.share', false);

  const [showTooltip, setShowTooltip] = React.useState(false);

  return (
    <div className={cx(styles.card)}>
      <div className={styles.icon} onClick={() => handleAddAsset(asset)}>
        <Thumbnail media={asset.icon} defaultImage="/images/assets/default_asset.jpg" />
        <div className={styles.fileType}>{fileType}</div>

        {internal &&
          <div className={styles.indicators}>
            <Tooltip content="Internal use only" placement="auto">
              <i className="paperflite-lock" />
            </Tooltip>
          </div>
        }

        <button className={styles.addIcon}>
          <i className={cx("paperflite-add", styles.addIcon)}></i>
        </button>
        <div className={styles.overlay}></div>

      </div>
      <div className={styles.cardContent}>
        <div className={styles.name}>
          <Text text={asset.name} lines={1} />
        </div>
      </div>
    </div>
  );
}


const Placeholder = () => {
  return (
    <div className={cx(styles.assetIcon, styles.placeholder)}>
      <div className={cx(styles.placeholderIcon, styles.block)}></div>
      <div className={styles.cardContent}>
        <div className={styles.details}>
          <h3 className={cx(styles.assetName, styles.block)}></h3>
          <p className={cx(styles.assetMetadata, styles.block)}></p>
        </div>
      </div>
    </div>
  );
}
